import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import FileUpload from "@/components/FileUpload.jsx";
import DropdownMulti from "@/components/DropdownMulti.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import { addToast } from "@/store/app/action.js";
import {
  barcodeLookup,
  createAdminProduct,
  getAdminAvailableCategories,
  searchUpcDatabase,
} from "@/api/masterMenu.js";
import { uploadFile } from "@/api/index.js";
import {
  getProductshotStatus,
  runProductshotAnalyze,
  runProductshotGenerate,
  enrichBarcodeLookup,
  b64ToBlob,
} from "@/api/productshot.js";
import MediaCarousel, {
  buildProductMediaUrls,
} from "@/components/MediaCarousel.jsx";
import {
  MockListingPreview,
  previewFromDraft,
  previewFromLookupData,
} from "./MasterMenuListingPreview.jsx";
import {
  draftRecordToFormDraft,
  publishMasterMenuDraft,
} from "./masterMenuPublish.js";

/** Fixed OpenAI image quality for catalog-style renders (no UI). */
const PRODUCTSHOT_IMAGE_QUALITY = "medium";

const emptyDraft = () => ({
  upc_code: "",
  title: "",
  subtitle: "",
  description: "",
  company: "",
  volume_label: "",
  sku: "",
  abv: "",
  image_small: "",
  image_large: "",
  additional_images: [],
  default_category: "",
  categories: [],
  disabled: false,
  no_tax: false,
  avalara_tax_code: "",
});

function asBoolean(val) {
  if (typeof val === "boolean") return val;
  const s = String(val || "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

function normalizeCategoryId(categoryLike) {
  if (!categoryLike) return "";
  if (typeof categoryLike === "string") return String(categoryLike).trim();
  return String(categoryLike.id || categoryLike._id || "").trim();
}

function normalizeCategoryLabel(categoryLike) {
  if (!categoryLike) return "";
  if (typeof categoryLike === "string") return String(categoryLike).trim();
  return String(categoryLike.name || categoryLike.label || "").trim();
}

function extractProductLike(data) {
  if (!data || typeof data !== "object") return {};
  if (data.product && typeof data.product === "object" && !Array.isArray(data.product)) {
    return data.product;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (data.item && typeof data.item === "object") return data.item;
  if (data.result && typeof data.result === "object") return data.result;
  const rest = { ...data };
  delete rest.success;
  delete rest.message;
  delete rest._debug;
  delete rest.query;
  return rest;
}

function mergeLookupIntoDraft(prev, data, enteredUpc) {
  const p = extractProductLike(data);
  const img0 = Array.isArray(p.images) && p.images[0];
  const fromImages =
    (typeof img0 === "string" && img0) || (img0 && typeof img0 === "object" && img0.url) || "";
  const firstImg = fromImages || (Array.isArray(p.image_urls) && p.image_urls[0]) || "";

  const next = { ...prev };
  const setIf = (key, val) => {
    const s = val != null ? String(val).trim() : "";
    if (s) next[key] = s;
  };

  setIf("upc_code", p.upc_code || p.upc || p.barcode || p.gtin || enteredUpc);
  setIf("title", p.title || p.name || p.product_name);
  setIf("subtitle", p.subtitle || p.sub_title);
  setIf("description", p.description || p.details);
  setIf("company", p.company || p.brand || p.manufacturer);
  setIf("volume_label", p.volume_label || p.volume || p.size);
  setIf("sku", p.sku || p.sku_code);
  setIf("image_small", p.image_small || p.thumbnail || p.image || firstImg);
  setIf("image_large", p.image_large || p.image || firstImg);
  const primaryImage = String(next.image_large || next.image_small || "").trim();
  const extraImages = Array.isArray(p.additional_images)
    ? p.additional_images.map((url) => String(url || "").trim()).filter(Boolean)
    : [];
  if (extraImages.length) {
    next.additional_images = extraImages.filter((url) => url && url !== primaryImage);
  } else if (Array.isArray(p.images) && p.images.length > 1) {
    next.additional_images = p.images
      .slice(1)
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object" && item.url) return String(item.url).trim();
        return "";
      })
      .filter((url) => url && url !== primaryImage);
  }
  setIf(
    "default_category",
    normalizeCategoryId(p.default_category) ||
      normalizeCategoryId(p.category) ||
      normalizeCategoryLabel(p.default_category) ||
      normalizeCategoryLabel(p.category)
  );
  if (Array.isArray(p.categories)) {
    next.categories = p.categories
      .map((c) => normalizeCategoryId(c) || normalizeCategoryLabel(c))
      .filter(Boolean);
  }
  if (p.disabled != null) {
    next.disabled = asBoolean(p.disabled);
  }
  if (p.no_tax != null) {
    next.no_tax = asBoolean(p.no_tax);
  }
  setIf("abv", p.abv);
  setIf("avalara_tax_code", p.avalara_tax_code || p.tax_code || p.avalaraTaxCode);
  if (
    !Array.isArray(next.additional_images) &&
    Array.isArray(p.additionalImages) &&
    p.additionalImages.length
  ) {
    next.additional_images = p.additionalImages
      .map((url) => String(url || "").trim())
      .filter(Boolean);
  }

  return next;
}

function pickUpcFromUpcDatabaseSearch(data) {
  if (!data || typeof data !== "object") return "";
  const rows = Array.isArray(data.suggestions) ? data.suggestions : [];
  for (const row of rows) {
    const b = String(row?.barcode || "").trim();
    if (b) return b;
  }
  const feeds = Array.isArray(data.retailer_feed_upcs) ? data.retailer_feed_upcs : [];
  for (const code of feeds) {
    const c = String(code || "").trim();
    if (c) return c;
  }
  return "";
}

function mergeProductshotAnalysis(prev, analysis, catalogImageUrl, opts = {}) {
  const { listingDescription = "", avalaraTaxCode = "" } = opts;
  const a = analysis && typeof analysis === "object" ? analysis : {};
  const bl = a.back_label && typeof a.back_label === "object" ? a.back_label : {};
  const next = { ...prev };
  const setIf = (key, val) => {
    const s = val != null ? String(val).trim() : "";
    if (s) next[key] = s;
  };
  setIf("title", a.brand);
  setIf("subtitle", a.product_name);
  setIf("company", a.brand);
  const aiListing = String(a.listing_description || "").trim();
  const listing =
    String(listingDescription || "").trim() || aiListing;
  if (listing) {
    next.description = listing;
  } else {
    const frontBits = Array.isArray(a.front_label?.text)
      ? a.front_label.text.filter(Boolean).join("\n")
      : "";
    const desc = [a.physical_description, frontBits].filter(Boolean).join("\n\n");
    setIf("description", desc);
  }
  setIf("upc_code", firstNonEmptyString(bl.upc_barcode, a.upc_identity_guess));
  next.volume_label = volumeLabelFromProductshotAnalysis(a);
  setIf("sku", bl.sku);
  setIf(
    "default_category",
    normalizeCategoryId(a.default_category) ||
      normalizeCategoryId(a.category) ||
      normalizeCategoryLabel(a.default_category) ||
      normalizeCategoryLabel(a.category)
  );
  if (Array.isArray(a.categories)) {
    next.categories = a.categories
      .map((c) => normalizeCategoryId(c) || normalizeCategoryLabel(c))
      .filter(Boolean);
  }
  if (a.disabled != null) {
    next.disabled = asBoolean(a.disabled);
  }
  if (a.no_tax != null) {
    next.no_tax = asBoolean(a.no_tax);
  }
  const abvFrom =
    a.abv ??
    bl.alcohol_content ??
    (a.back_label && typeof a.back_label === "object" ? a.back_label.alcohol_content : null);
  if (abvFrom != null && String(abvFrom).trim()) {
    setIf("abv", String(abvFrom).replace(/%/g, "").trim());
  }
  const aiTax = String(a.avalara_tax_code || "").trim();
  const tax = String(avalaraTaxCode || "").trim() || aiTax;
  if (tax) next.avalara_tax_code = tax;
  if (catalogImageUrl) {
    next.image_small = catalogImageUrl;
    next.image_large = catalogImageUrl;
  }
  return next;
}

function volumeLabelFromProductshotAnalysis(a) {
  if (!a || typeof a !== "object") return "Each";
  const bl = a.back_label && typeof a.back_label === "object" ? a.back_label : {};
  const v = String(a.volume_label || "").trim() || String(bl.volume_weight || "").trim();
  return v || "Each";
}

function firstNonEmptyString(...vals) {
  for (const v of vals) {
    const s = v != null ? String(v).trim() : "";
    if (s) return s;
  }
  return "";
}

function listingDescriptionFromProductshotAnalysis(a) {
  if (!a || typeof a !== "object") return "";
  const frontBits = Array.isArray(a.front_label?.text)
    ? a.front_label.text.filter(Boolean).join("\n")
    : "";
  const suggestedDesc = [a.physical_description, frontBits].filter(Boolean).join("\n\n");
  const aiListing = String(a.listing_description || "").trim();
  return aiListing || String(suggestedDesc || "").trim();
}

function upcDatabaseSearchParamsFromAnalysis(analysis, fallbackTitle, fallbackSubtitle) {
  const a = analysis && typeof analysis === "object" ? analysis : {};
  const title = firstNonEmptyString(a.upc_search_title, fallbackTitle);
  const subtitle = firstNonEmptyString(a.upc_search_subtitle, fallbackSubtitle);
  return { title, subtitle };
}

function normalizeAvailableCategories(data) {
  const raw = Array.isArray(data?.categories) ? data.categories : [];
  return raw
    .map((c) => {
      const id = normalizeCategoryId(c);
      const name = normalizeCategoryLabel(c);
      const rowType = String(c?.type || "").trim();
      if (!id || !name || rowType === "divider") return null;
      const tag = String(c?.tag || "").trim();
      return { id, name, tag, is_alcohol: Boolean(c?.is_alcohol) };
    })
    .filter(Boolean);
}

function resolveCategoryId(input, options) {
  const value = String(input || "").trim();
  if (!value) return "";
  const byId = options.find((c) => c.id === value);
  if (byId) return byId.id;
  const needle = value.toLowerCase();
  const byName = options.find((c) => c.name.toLowerCase() === needle);
  return byName ? byName.id : "";
}

function categoryTagForId(categoryId, options) {
  const id = String(categoryId || "").trim();
  if (!id) return "";
  const row = options.find((c) => c.id === id);
  return row && row.tag ? String(row.tag).trim() : "";
}

function previewFromProductshotPayload(payload) {
  if (!payload || !payload.analysis) {
    return null;
  }
  const a = payload.analysis;
  const img =
    payload.image ||
    (payload.b64 ? `data:image/png;base64,${payload.b64}` : "");
  const brand = String(a.brand || "").trim();
  const product = String(a.product_name || "").trim();
  const headline =
    [brand, product].filter(Boolean).join(" – ") || product || brand || "Preview";
  const vol = volumeLabelFromProductshotAnalysis(a);
  const desc = listingDescriptionFromProductshotAnalysis(a);
  return { imageUrl: img, title: headline, vol, desc };
}

function buildCatalogImagePromptFromDraft(draft) {
  const title = String(draft.title || "").trim();
  const subtitle = String(draft.subtitle || "").trim();
  const company = String(draft.company || "").trim();
  const volume = String(draft.volume_label || "").trim();
  const desc = String(draft.description || "").trim();
  const labelLine = [company, title, subtitle].filter(Boolean).join(" - ");
  return [
    "Create a clean e-commerce catalog product image on plain white background.",
    "Single centered product, photoreal, studio lighting, no watermark, no extra props.",
    labelLine ? `Product: ${labelLine}.` : "",
    volume ? `Package size text should read: ${volume}.` : "",
    desc ? `Context: ${desc.slice(0, 400)}.` : "",
    "Output should look like a polished listing image suitable for online grocery.",
  ]
    .filter(Boolean)
    .join(" ");
}

const MasterMenuCreateProduct = ({
  embedded = false,
  initialAutocreateSeed = null,
  initialDraftRecord = null,
  draftProductId = null,
  draftEditMode = false,
  onClose = null,
  onCreated = null,
  onPublished = null,
}) => {
  const isDraftEdit = Boolean(initialDraftRecord);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLocation } = useSelector((state) => state.app);

  const [draft, setDraft] = useState(() =>
    isDraftEdit ? draftRecordToFormDraft(initialDraftRecord) : emptyDraft()
  );
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupApplyLoading, setLookupApplyLoading] = useState(false);
  const [lookupImageGenerating, setLookupImageGenerating] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupCacheMeta, setLookupCacheMeta] = useState({ cached: false, checkedAt: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingLookupData, setPendingLookupData] = useState(null);

  const [productshotFile, setProductshotFile] = useState(null);
  const [productshotError, setProductshotError] = useState(null);
  const [productshotModalOpen, setProductshotModalOpen] = useState(false);
  const [pendingProductshot, setPendingProductshot] = useState(null);
  const [productshotApplyLoading, setProductshotApplyLoading] = useState(false);
  const [productshotConfigured, setProductshotConfigured] = useState(false);
  const [productshotAvalaraTaxCode, setProductshotAvalaraTaxCode] = useState("");
  const [creationMode, setCreationMode] = useState(isDraftEdit ? "manual" : null);
  const [formUnlocked, setFormUnlocked] = useState(isDraftEdit);
  const [seededUpcFlow, setSeededUpcFlow] = useState(false);
  const [productshotAnalyzeLoading, setProductshotAnalyzeLoading] = useState(false);
  const [productshotImageGenerating, setProductshotImageGenerating] = useState(false);
  const [draftPreviewModalOpen, setDraftPreviewModalOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);

  const lastLookedUpUpcRef = useRef("");
  const consumedAutocreateSeedRef = useRef(false);
  const consumedInitialSeedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getProductshotStatus()
      .then((d) => {
        if (!cancelled) setProductshotConfigured(Boolean(d?.configured));
      })
      .catch(() => {
        if (!cancelled) setProductshotConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    let cancelled = false;
    getAdminAvailableCategories()
      .then((data) => {
        if (!cancelled) {
          setAvailableCategories(normalizeAvailableCategories(data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableCategories([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLocation]);

  const pendingPreview = useMemo(
    () => (pendingLookupData ? previewFromLookupData(pendingLookupData) : null),
    [pendingLookupData]
  );
  const pendingPreviewHasImage = Boolean(
    String(pendingPreview?.imageUrl || "").trim() ||
      (Array.isArray(pendingPreview?.mediaUrls) && pendingPreview.mediaUrls.length > 0)
  );

  const pendingProductshotPreview = useMemo(
    () => (pendingProductshot ? previewFromProductshotPayload(pendingProductshot) : null),
    [pendingProductshot]
  );

  const draftListingPreview = useMemo(() => previewFromDraft(draft), [draft]);
  const draftMediaUrls = useMemo(
    () =>
      buildProductMediaUrls(
        String(draft.image_large || draft.image_small || "").trim(),
        draft.additional_images
      ),
    [draft.image_large, draft.image_small, draft.additional_images]
  );
  const cachedLookupDateLabel = useMemo(() => {
    if (!lookupCacheMeta.cached || !lookupCacheMeta.checkedAt) return "";
    const dt = new Date(lookupCacheMeta.checkedAt);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString();
  }, [lookupCacheMeta]);
  const categoryNameOptions = useMemo(
    () => availableCategories.map((c) => c.name),
    [availableCategories]
  );
  const categoryDropdownOptions = useMemo(
    () =>
      availableCategories
        .filter((c) => c.id !== draft.default_category)
        .map((c) => ({ id: c.id, label: c.name })),
    [availableCategories, draft.default_category]
  );

  const alcoholCategorySelected = useMemo(() => {
    const cats = Array.isArray(draft.categories) ? draft.categories : [];
    const ids = new Set(
      [String(draft.default_category || "").trim(), ...cats.map((c) => String(c || "").trim())].filter(
        Boolean
      )
    );
    for (const id of ids) {
      if (availableCategories.find((c) => c.id === id)?.is_alcohol) return true;
    }
    return false;
  }, [draft.default_category, draft.categories, availableCategories]);

  useEffect(() => {
    if (alcoholCategorySelected) return;
    setDraft((d) => {
      if (!String(d.abv || "").trim()) return d;
      return { ...d, abv: "" };
    });
  }, [alcoholCategorySelected]);

  useEffect(() => {
    if (!availableCategories.length) return;
    setDraft((prev) => {
      const resolvedDefault = resolveCategoryId(prev.default_category, availableCategories);
      const resolvedCategories = (Array.isArray(prev.categories) ? prev.categories : [])
        .map((c) => resolveCategoryId(c, availableCategories))
        .filter(Boolean);
      const defaultToUse = resolvedDefault || prev.default_category;
      const nextCategories = Array.from(new Set(resolvedCategories)).filter(
        (cid) => cid && cid !== defaultToUse
      );
      if (
        defaultToUse === prev.default_category &&
        JSON.stringify(nextCategories) === JSON.stringify(prev.categories || [])
      ) {
        return prev;
      }
      return {
        ...prev,
        default_category: defaultToUse,
        categories: nextCategories,
      };
    });
  }, [availableCategories]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPendingLookupData(null);
  }, []);

  const closeProductshotModal = useCallback(() => {
    setProductshotModalOpen(false);
    setPendingProductshot(null);
    setProductshotAvalaraTaxCode("");
    setProductshotAnalyzeLoading(false);
    setProductshotImageGenerating(false);
  }, []);

  const beginCreationMode = useCallback(
    (mode) => {
      if (mode === "image" && !productshotConfigured) {
        dispatch(
          addToast({
            type: "error",
            message: "Photo autocreate is not available. Contact support if you need it turned on.",
            timer: 6,
          })
        );
        return;
      }
      setCreationMode(mode);
      setFormUnlocked(mode === "manual");
      setSeededUpcFlow(false);
      setDraft(emptyDraft());
      setLookupError(null);
      setProductshotError(null);
      setProductshotFile(null);
      setPendingLookupData(null);
      setPreviewOpen(false);
      lastLookedUpUpcRef.current = "";
    },
    [productshotConfigured, dispatch]
  );

  const handleImageAutocreate = useCallback(async () => {
    if (!productshotFile) {
      dispatch(
        addToast({
          type: "error",
          message: "Choose a product photo first.",
          timer: 4,
        })
      );
      return;
    }
    setProductshotError(null);
    setProductshotAnalyzeLoading(true);
    let analysisObj = null;
    try {
      const data = await runProductshotAnalyze(productshotFile, {
        available_categories: categoryNameOptions,
      });
      analysisObj = data.analysis && typeof data.analysis === "object" ? data.analysis : null;
      if (!analysisObj) {
        throw new Error("NO_ANALYSIS");
      }
      const a = analysisObj;
      const aiTax = String(a.avalara_tax_code || "").trim();
      setProductshotAvalaraTaxCode(aiTax || "");
      setPendingProductshot({
        analysis: analysisObj,
        image: null,
        b64: null,
      });
      setProductshotModalOpen(true);
    } catch (e) {
      setProductshotError(
        e?.message === "NO_ANALYSIS"
          ? "We could not read details from that photo. Try a clearer picture."
          : "We could not read that photo. Try another picture or try again later."
      );
      setProductshotAnalyzeLoading(false);
      return;
    }
    setProductshotAnalyzeLoading(false);

    const gp = String(analysisObj.generation_prompt || "").trim();
    if (!gp) {
      dispatch(
        addToast({
          type: "error",
          message: "We could not build a catalog-style picture from this photo. Try another angle.",
          timer: 5,
        })
      );
      return;
    }

    setProductshotImageGenerating(true);
    try {
      const gen = await runProductshotGenerate({
        generation_prompt: gp,
        quality: PRODUCTSHOT_IMAGE_QUALITY,
        size: "1024x1024",
      });
      setPendingProductshot((prev) =>
        prev ? { ...prev, image: gen.image || null, b64: gen.b64 || null } : prev
      );
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message: "The catalog-style picture did not finish. Try again in a moment.",
          timer: 6,
        })
      );
    } finally {
      setProductshotImageGenerating(false);
    }
  }, [productshotFile, dispatch, categoryNameOptions]);

  const applyProductshot = useCallback(async () => {
    if (!pendingProductshot?.analysis) return;
    setProductshotApplyLoading(true);
    try {
      let catalogUrl = pendingProductshot.image || null;
      if (!catalogUrl && pendingProductshot.b64) {
        const blob = b64ToBlob(pendingProductshot.b64);
        const file = new File([blob], "catalog-shot.png", { type: "image/png" });
        const uploaded = await uploadFile(file, "product");
        catalogUrl = uploaded.url;
      }
      const listingDescription = listingDescriptionFromProductshotAnalysis(
        pendingProductshot.analysis
      );
      const merged = mergeProductshotAnalysis(
        draftRef.current,
        pendingProductshot.analysis,
        catalogUrl,
        {
          listingDescription,
          avalaraTaxCode: productshotAvalaraTaxCode,
        }
      );
      setDraft(merged);

      const title = String(merged.title || "").trim();
      const subtitle = String(merged.subtitle || "").trim();
      const searchParams = upcDatabaseSearchParamsFromAnalysis(
        pendingProductshot.analysis,
        title,
        subtitle
      );
      const nameQuery = [searchParams.title, searchParams.subtitle].filter(Boolean).join(" ").trim();
      let addedUpc = String(merged.upc_code || "").trim();
      let upcFromNameSearch = false;
      if (!addedUpc && nameQuery.length >= 3) {
        try {
          const searchData = await searchUpcDatabase({
            title: searchParams.title,
            subtitle: searchParams.subtitle,
            page: 1,
          });
          const picked = pickUpcFromUpcDatabaseSearch(searchData);
          if (picked) {
            setDraft((d) => {
              if (String(d.upc_code || "").trim()) return d;
              return { ...d, upc_code: picked };
            });
            lastLookedUpUpcRef.current = picked;
            addedUpc = picked;
            upcFromNameSearch = true;
          }
        } catch (e) {
          const res = e.response;
          const body = res?.data || {};
          if (res?.status === 503 && body.error === "not_configured") {
            dispatch(
              addToast({
                type: "error",
                message:
                  body.message ||
                  "Automatic UPC lookup by name is not set up. You can type the UPC yourself.",
                timer: 6,
              })
            );
          }
        }
      }

      const successMessage =
        addedUpc && upcFromNameSearch
          ? "Details from the photo were saved. A UPC was filled in when we found a close match."
          : addedUpc
            ? "Details from the photo were saved. UPC was read from the barcode on the photo."
            : "Details from the photo were saved.";

      dispatch(
        addToast({
          type: "success",
          message: successMessage,
          timer: 4,
        })
      );
      setFormUnlocked(true);
      closeProductshotModal();
      setProductshotFile(null);
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message: "Something went wrong while saving. Try again.",
          timer: 6,
        })
      );
    } finally {
      setProductshotApplyLoading(false);
    }
  }, [pendingProductshot, productshotAvalaraTaxCode, dispatch, closeProductshotModal]);

  const runUpcLookup = useCallback(async ({ upcInput, forceRefetch = false, additionalClue = "" } = {}) => {
    const upc = String(upcInput || "").trim();
    setLookupError(null);
    if (!upc) {
      lastLookedUpUpcRef.current = "";
      setLookupCacheMeta({ cached: false, checkedAt: "" });
      return;
    }
    if (!forceRefetch && upc === lastLookedUpUpcRef.current) {
      return;
    }

    setLookupLoading(true);
    try {
      const data = await barcodeLookup(upc, {
        force_refetch: forceRefetch,
        additional_clue: additionalClue,
      });
      setLookupCacheMeta({
        cached: Boolean(data?.cached),
        checkedAt: String(data?.cached_checked_at || ""),
      });
      if (data && data.success === false) {
        setLookupError(data.message || "No match.");
        lastLookedUpUpcRef.current = upc;
        return;
      }
      const p = extractProductLike(data);
      const hasPayload =
        p &&
        (String(p.title || p.name || p.product_name || "").trim() ||
          String(p.subtitle || "").trim() ||
          String(p.description || p.details || "").trim() ||
          p.image_large ||
          p.image_small ||
          p.image ||
          (Array.isArray(p.images) && p.images.length) ||
          (Array.isArray(p.additional_images) && p.additional_images.length));
      if (!hasPayload) {
        setLookupError("No match.");
        lastLookedUpUpcRef.current = upc;
        return;
      }
      setPendingLookupData(data);
      setPreviewOpen(true);
      lastLookedUpUpcRef.current = upc;
    } catch (e) {
      const res = e.response;
      const body = res?.data || {};
      setLookupCacheMeta({ cached: false, checkedAt: "" });
      setLookupError(body.message || body.error || e.message || "Lookup failed.");
      lastLookedUpUpcRef.current = upc;
    } finally {
      setLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    if (embedded) return;
    if (consumedAutocreateSeedRef.current) return;
    const seedState = location.state;
    if (!seedState || !seedState.autocreateFromUPC) return;

    consumedAutocreateSeedRef.current = true;
    setSeededUpcFlow(true);
    beginCreationMode("upc");

    const seed = seedState.autocreateSeed || {};
    const seededUpc = String(seed.upc_code || "").trim();
    setDraft((prev) => ({
      ...prev,
      upc_code: seededUpc,
      title: String(seed.title || "").trim() || prev.title,
      subtitle: String(seed.subtitle || "").trim() || prev.subtitle,
      company: String(seed.company || "").trim() || prev.company,
    }));

    if (seededUpc) {
      const clue = [seed.title, seed.subtitle, seed.company].filter(Boolean).join(" ").trim();
      runUpcLookup({ upcInput: seededUpc, forceRefetch: false, additionalClue: clue });
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [embedded, location.state, location.pathname, beginCreationMode, runUpcLookup, navigate]);

  useEffect(() => {
    if (!embedded) return;
    if (consumedInitialSeedRef.current) return;
    if (!initialAutocreateSeed) return;

    consumedInitialSeedRef.current = true;
    setSeededUpcFlow(true);
    beginCreationMode("upc");

    const seed = initialAutocreateSeed || {};
    const seededUpc = String(seed.upc_code || "").trim();
    setDraft((prev) => ({
      ...prev,
      upc_code: seededUpc,
      title: String(seed.title || "").trim() || prev.title,
      subtitle: String(seed.subtitle || "").trim() || prev.subtitle,
      company: String(seed.company || "").trim() || prev.company,
    }));

    if (seededUpc) {
      const clue = [seed.title, seed.subtitle, seed.company].filter(Boolean).join(" ").trim();
      runUpcLookup({ upcInput: seededUpc, forceRefetch: false, additionalClue: clue });
    }
  }, [embedded, initialAutocreateSeed, beginCreationMode, runUpcLookup]);

  const handleUpcBlur = useCallback(async (e) => {
    if (creationMode === "image" && !formUnlocked) {
      return;
    }
    await runUpcLookup({ upcInput: e.target.value, forceRefetch: false });
  }, [creationMode, formUnlocked, runUpcLookup]);

  const applyPendingLookup = useCallback(async () => {
    if (!pendingLookupData) return;
    const upc = String(draftRef.current.upc_code || "").trim();
    setLookupApplyLoading(true);
    try {
      let next = mergeLookupIntoDraft(draftRef.current, pendingLookupData, upc);
      if (!String(next.avalara_tax_code || "").trim()) {
        try {
          const enriched = await enrichBarcodeLookup({
            product: next,
            available_categories: availableCategories.map((c) => ({
              id: c.id,
              name: c.name,
            })),
          });
          const patch = enriched?.product && typeof enriched.product === "object" ? enriched.product : {};
          next = { ...next, ...patch };
        } catch {
          /* optional: barcode data may be enough without enrich */
        }
      }
      const hasLookupImage = String(next.image_small || "").trim() || String(next.image_large || "").trim();
      if (!hasLookupImage) {
        try {
          const generation_prompt = buildCatalogImagePromptFromDraft(next);
          if (generation_prompt && productshotConfigured) {
            const gen = await runProductshotGenerate({
              generation_prompt,
              quality: PRODUCTSHOT_IMAGE_QUALITY,
              size: "1024x1024",
            });
            let generatedUrl = String(gen?.image || "").trim();
            if (!generatedUrl && gen?.b64) {
              const blob = b64ToBlob(gen.b64);
              const file = new File([blob], "barcode-lookup-generated.png", { type: "image/png" });
              const uploaded = await uploadFile(file, "product");
              generatedUrl = String(uploaded?.url || "").trim();
            }
            if (generatedUrl) {
              next = { ...next, image_small: generatedUrl, image_large: generatedUrl };
            }
          }
        } catch (e) {
          // Best-effort fallback only; keep manual flow moving even if image generation fails.
        }
      }

      setDraft(next);
      setFormUnlocked(true);
      closePreview();
    } finally {
      setLookupApplyLoading(false);
    }
  }, [pendingLookupData, closePreview, categoryNameOptions, availableCategories, productshotConfigured]);

  const generateLookupPreviewImage = useCallback(async () => {
    if (!pendingLookupData || lookupImageGenerating) return;
    const upc = String(draftRef.current.upc_code || "").trim();
    if (!upc) return;
    setLookupImageGenerating(true);
    try {
      const seeded = mergeLookupIntoDraft(draftRef.current, pendingLookupData, upc);
      const generation_prompt = buildCatalogImagePromptFromDraft(seeded);
      const gen = await runProductshotGenerate({
        generation_prompt,
        quality: PRODUCTSHOT_IMAGE_QUALITY,
        size: "1024x1024",
      });
      let generatedUrl = String(gen?.image || "").trim();
      if (!generatedUrl && gen?.b64) {
        const blob = b64ToBlob(gen.b64);
        const file = new File([blob], "barcode-lookup-preview-generated.png", { type: "image/png" });
        const uploaded = await uploadFile(file, "product");
        generatedUrl = String(uploaded?.url || "").trim();
      }
      if (!generatedUrl) {
        throw new Error("No image returned from AI generation.");
      }

      setPendingLookupData((prev) => {
        const prevProduct = extractProductLike(prev);
        return {
          ...(prev || {}),
          product: {
            ...prevProduct,
            upc_code: String(prevProduct?.upc_code || upc).trim(),
            image_small: generatedUrl,
            image_large: generatedUrl,
            image: generatedUrl,
            images: [generatedUrl],
          },
        };
      });

      // Persist generated image into barcode lookup cache for this UPC.
      try {
        await barcodeLookup(upc, { cache_image_url: generatedUrl, force_refetch: false });
      } catch (e) {
        // Best effort cache persistence; do not block UI success.
      }

      dispatch(
        addToast({
          type: "success",
          message: "AI image generated and saved to lookup cache.",
          timer: 4,
        })
      );
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message: e?.message || "AI image generation failed.",
          timer: 6,
        })
      );
    } finally {
      setLookupImageGenerating(false);
    }
  }, [pendingLookupData, lookupImageGenerating, dispatch]);

  const handleUpcChange = useCallback((e) => {
    const v = e.target.value;
    setDraft((d) => ({ ...d, upc_code: v }));
    setLookupError(null);
    setLookupCacheMeta({ cached: false, checkedAt: "" });
  }, []);

  const handleSave = async () => {
    if (draftEditMode || isDraftEdit) {
      const draftId = String(draftProductId || initialDraftRecord?.id || "").trim();
      if (!draftId) {
        dispatch(
          addToast({
            type: "error",
            message: "Missing draft product id.",
            timer: 4,
          })
        );
        return;
      }

      setSaveLoading(true);
      try {
        const created = await publishMasterMenuDraft({
          draftId,
          draft: draftRef.current,
          availableCategories,
          draftSkuId: String(initialDraftRecord?.sku_id || "").trim(),
        });
        dispatch(
          addToast({
            type: "success",
            message: created?.sku_id
              ? `Published (${created.sku_id})`
              : "Product published.",
            timer: 5,
          })
        );
        onPublished?.(created);
        onClose?.();
      } catch (e) {
        if (e?.createdProduct?.sku_id) {
          dispatch(
            addToast({
              type: "warning",
              message: e.message,
              timer: 8,
            })
          );
          onPublished?.(e.createdProduct);
          onClose?.();
          return;
        }
        dispatch(
          addToast({
            type: "error",
            message: e?.message || "Publish failed.",
            timer: 8,
          })
        );
      } finally {
        setSaveLoading(false);
      }
      return;
    }

    const upc = String(draft.upc_code || "").trim();
    if (!upc) {
      dispatch(
        addToast({
          type: "error",
          message: "UPC required.",
          timer: 4,
        })
      );
      return;
    }
    if (!String(draft.title || "").trim()) {
      dispatch(
        addToast({
          type: "error",
          message: "Name required.",
          timer: 4,
        })
      );
      return;
    }

    setSaveLoading(true);
    try {
      const defaultId = String(draft.default_category || "").trim();
      if (!defaultId) {
        dispatch(
          addToast({
            type: "error",
            message: "Default category is required.",
            timer: 4,
          })
        );
        setSaveLoading(false);
        return;
      }
      const typeTag = categoryTagForId(defaultId, availableCategories);
      if (!typeTag) {
        dispatch(
          addToast({
            type: "error",
            message: "Default category must have a tag. Try reloading categories or pick another default.",
            timer: 6,
          })
        );
        setSaveLoading(false);
        return;
      }
      const otherCategoryIds = (Array.isArray(draft.categories) ? draft.categories : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
        .filter((id) => id !== defaultId);
      const category_tags = Array.from(
        new Set(
          otherCategoryIds
            .map((id) => categoryTagForId(id, availableCategories))
            .filter(Boolean)
        )
      );

      const payload = {
        upc_code: upc,
        title: String(draft.title || "").trim(),
        subtitle: String(draft.subtitle || "").trim(),
        description: String(draft.description || "").trim(),
        company: String(draft.company || "").trim(),
        volume: String(draft.volume_label || "").trim(),
        sku: String(draft.sku || "").trim() || undefined,
        image_small: String(draft.image_small || "").trim() || undefined,
        image_large: String(draft.image_large || "").trim() || undefined,
        ...(Array.isArray(draft.additional_images) && draft.additional_images.length
          ? {
              additional_images: draft.additional_images
                .map((url) => String(url || "").trim())
                .filter(Boolean),
            }
          : {}),
        type: typeTag,
        ...(category_tags.length ? { category_tags } : {}),
        disabled: Boolean(draft.disabled),
        no_tax: Boolean(draft.no_tax),
        avalara_tax_code: String(draft.avalara_tax_code || "").trim() || undefined,
      };
      if (alcoholCategorySelected) {
        const abvRaw = String(draft.abv || "").trim();
        if (abvRaw) {
          const n = parseFloat(abvRaw.replace(/%$/, "").trim());
          payload.abv = Number.isFinite(n) ? n : abvRaw;
        }
      }
      const result = await createAdminProduct(payload);
      dispatch(
        addToast({
          type: "success",
          message:
            result?.message ||
            (result?.sku_id ? `Created (${result.sku_id})` : "Saved."),
          timer: 5,
        })
      );
      if (embedded) {
        onCreated?.(result);
        onClose?.();
      } else {
        navigate("/catalog/master-menu");
      }
    } catch (e) {
      const body = e.response?.data || {};
      dispatch(
        addToast({
          type: "error",
          message: body.message || body.error || e.message || "Save failed.",
          timer: 8,
        })
      );
    } finally {
      setSaveLoading(false);
    }
  };

  if (!selectedLocation) {
    return null;
  }

  return (
    <div className={`${embedded ? "px-0 py-0 w-full max-w-none h-full" : "px-4 sm:px-6 lg:px-8 py-8 w-full max-w-5xl mx-auto"}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          {!embedded && (
            <Link
              to="/catalog/master-menu"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              ← Master Menu
            </Link>
          )}
          <h1 className="mt-2 text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            {isDraftEdit ? "Edit draft product" : "New product"}
          </h1>
        </div>
        <div className="flex gap-2">
          <SecondaryButton type="button" onClick={() => (embedded ? onClose?.() : navigate("/catalog/master-menu"))}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSave}
            disabled={
              saveLoading ||
              (!isDraftEdit && (!creationMode || (creationMode !== "manual" && !formUnlocked)))
            }
          >
            {saveLoading ? (isDraftEdit ? "Publishing…" : "Saving…") : isDraftEdit ? "Publish" : "Save"}
          </PrimaryButton>
        </div>
      </div>

      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-6 space-y-6">
        {creationMode === null && !isDraftEdit ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Choose how you want to create this master menu product.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => beginCreationMode("upc")}
                className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/50 p-4 text-left transition hover:border-indigo-400 hover:ring-1 hover:ring-indigo-400/30"
              >
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Autocreate from UPC
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Enter a UPC, check the match in the preview, then continue to the full form.
                </p>
              </button>
              <button
                type="button"
                onClick={() => beginCreationMode("image")}
                className={`rounded-lg border p-4 text-left transition ${
                  productshotConfigured
                    ? "border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/50 hover:border-indigo-400 hover:ring-1 hover:ring-indigo-400/30"
                    : "cursor-not-allowed border-slate-100 bg-slate-100/80 opacity-60 dark:border-slate-700 dark:bg-slate-900/40"
                }`}
              >
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Autocreate from image
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Upload a product photo. We fill in the details and add a clean catalog-style picture
                  for you to review.
                </p>
              </button>
              <button
                type="button"
                onClick={() => beginCreationMode("manual")}
                className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/50 p-4 text-left transition hover:border-indigo-400 hover:ring-1 hover:ring-indigo-400/30"
              >
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Manual</div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Enter everything yourself. You can still look up a product when you type a UPC.
                </p>
              </button>
            </div>
          </div>
        ) : (
          <>
            {!formUnlocked ? (
              <div className="flex flex-wrap justify-end gap-2">
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setCreationMode(null);
                    setFormUnlocked(false);
                    setDraft(emptyDraft());
                    setLookupError(null);
                    setProductshotError(null);
                    setProductshotFile(null);
                    lastLookedUpUpcRef.current = "";
                  }}
                >
                  Back to setup
                </SecondaryButton>
              </div>
            ) : null}

            {(creationMode !== "image" || formUnlocked) && (
              <div>
                <label
                  htmlFor="master-create-upc"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
                >
                  UPC
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 min-w-0">
                    <input
                      id="master-create-upc"
                      className="form-input w-full font-mono pr-10"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={draft.upc_code}
                      onChange={handleUpcChange}
                      onBlur={handleUpcBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      disabled={lookupLoading}
                    />
                    {lookupLoading ? (
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex h-5 w-5 shrink-0">
                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-indigo-600" />
                      </span>
                    ) : null}
                  </div>
                </div>
                {creationMode === "upc" && !formUnlocked ? (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Enter a UPC, then leave this field (or press Enter) to look up. Apply from the
                    preview to unlock the rest of the form.
                  </p>
                ) : null}
                {lookupError ? (
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <p className="text-sm text-red-600 dark:text-red-400">{lookupError}</p>
                    {creationMode === "upc" && !formUnlocked && seededUpcFlow ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                        onClick={() => setFormUnlocked(true)}
                      >
                        Continue manually
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {lookupCacheMeta.cached ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Cached from previous search{cachedLookupDateLabel ? ` on ${cachedLookupDateLabel}` : ""}.
                    </p>
                    <button
                      type="button"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                      disabled={lookupLoading}
                      onClick={() =>
                        runUpcLookup({
                          upcInput: draft.upc_code,
                          forceRefetch: true,
                        })
                      }
                    >
                      Refetch
                    </button>
                  </div>
                ) : null}
                {formUnlocked ? (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      SKU
                    </label>
                    <input
                      className="form-input w-full"
                      value={draft.sku}
                      onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value }))}
                      disabled
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Autogenerated after creation
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {creationMode === "image" && !formUnlocked && productshotConfigured ? (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-3">
                <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Autocreate from image
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Use a clear photo of the front label. We read the label, then build a catalog-style
                  picture. A preview opens when the first step is done; wait until the picture
                  finishes loading before you apply.
                </p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
                  <div className="w-full min-w-0 max-w-2xl">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Photo
                    </label>
                    <div
                      className={
                        productshotAnalyzeLoading || productshotImageGenerating
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                    >
                      <FileUpload
                        key={
                          productshotFile
                            ? `${productshotFile.name}-${productshotFile.size}-${productshotFile.lastModified}`
                            : "no-photo"
                        }
                        accept={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
                        multiple={false}
                        onChange={(files) => {
                          const f = files && files[0] ? files[0] : null;
                          setProductshotFile(f);
                          setProductshotError(null);
                        }}
                        inputContent={
                          productshotFile
                            ? `${productshotFile.name} — drop or click to replace`
                            : "Drag and drop a product photo here, or click to browse (JPEG, PNG, or WebP)"
                        }
                      />
                    </div>
                    {productshotFile ? (
                      <button
                        type="button"
                        className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50"
                        disabled={productshotAnalyzeLoading || productshotImageGenerating}
                        onClick={() => {
                          setProductshotFile(null);
                          setProductshotError(null);
                        }}
                      >
                        Clear photo
                      </button>
                    ) : null}
                  </div>
                  <PrimaryButton
                    type="button"
                    disabled={
                      productshotAnalyzeLoading ||
                      productshotImageGenerating ||
                      !productshotFile
                    }
                    onClick={handleImageAutocreate}
                  >
                    {productshotAnalyzeLoading
                      ? "Reading photo…"
                      : productshotImageGenerating
                        ? "Building picture…"
                        : "Start"}
                  </PrimaryButton>
                </div>
                {productshotError ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{productshotError}</p>
                ) : null}
              </div>
            ) : null}

            {formUnlocked ? (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Product details
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch
                        checked={Boolean(draft.disabled)}
                        onChange={(checked) => setDraft((d) => ({ ...d, disabled: checked }))}
                        label="Globally disabled"
                        onLabel="Globally disabled"
                        offLabel="Globally disabled"
                      />
                    </div>
                    <SecondaryButton type="button" onClick={() => setDraftPreviewModalOpen(true)}>
                      Show listing preview
                    </SecondaryButton>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Name
                    </label>
                    <input
                      className="form-input w-full"
                      value={draft.title}
                      onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Brand
                    </label>
                    <input
                      className="form-input w-full"
                      value={draft.company}
                      onChange={(e) => setDraft((d) => ({ ...d, company: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Subtitle
                    </label>
                    <input
                      className="form-input w-full"
                      value={draft.subtitle}
                      onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Volume
                    </label>
                    <input
                      className="form-input w-full"
                      value={draft.volume_label}
                      onChange={(e) => setDraft((d) => ({ ...d, volume_label: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Default Category
                    </label>
                    <select
                      className="form-select w-full"
                      value={draft.default_category}
                      onChange={(e) =>
                        setDraft((d) => {
                          const value = e.target.value;
                          const categories = Array.isArray(d.categories) ? d.categories : [];
                          return {
                            ...d,
                            default_category: value,
                            categories: categories.filter((cid) => cid !== value),
                          };
                        })
                      }
                    >
                      <option value="">Select a category</option>
                      {availableCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Avalara tax code
                    </label>
                    <input
                      className="form-input w-full font-mono text-sm"
                      value={draft.avalara_tax_code}
                      onChange={(e) => setDraft((d) => ({ ...d, avalara_tax_code: e.target.value }))}
                      placeholder="e.g. P0000000"
                      autoComplete="off"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Avalara system tax code for this SKU (
                      <a
                        href="https://taxcode.avatax.avalara.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                      >
                        tax code reference
                      </a>
                      ).
                    </p>
                    <div className="mt-2">
                      <ToggleSwitch
                        checked={Boolean(draft.no_tax)}
                        onChange={(checked) => setDraft((d) => ({ ...d, no_tax: checked }))}
                        label="No Tax"
                        onLabel="No Tax"
                        offLabel="No Tax"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Other Categories
                    </label>
                    <DropdownMulti
                      options={categoryDropdownOptions}
                      selectedValues={(Array.isArray(draft.categories) ? draft.categories : []).filter(
                        (id) => id && id !== draft.default_category
                      )}
                      setSelectedValues={(values) => {
                        setDraft((d) => ({
                          ...d,
                          categories: values,
                        }));
                      }}
                      emptyPlaceholder="Select categories"
                    />
                  </div>
                  {alcoholCategorySelected ? (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                        ABV
                      </label>
                      <input
                        className="form-input w-full"
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder="e.g. 5.2"
                        value={draft.abv}
                        onChange={(e) => setDraft((d) => ({ ...d, abv: e.target.value }))}
                      />
                    </div>
                  ) : null}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Description
                    </label>
                    <textarea
                      className="form-input w-full min-h-[120px] resize-y"
                      value={draft.description}
                      onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Image (small) URL
                    </label>
                    <input
                      className="form-input w-full text-sm"
                      value={draft.image_small}
                      onChange={(e) => setDraft((d) => ({ ...d, image_small: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Image (large) URL
                    </label>
                    <input
                      className="form-input w-full text-sm"
                      value={draft.image_large}
                      onChange={(e) => setDraft((d) => ({ ...d, image_large: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                      Additional images
                    </label>
                    <textarea
                      className="form-input w-full min-h-[88px] resize-y text-sm font-mono"
                      placeholder="One image or video URL per line"
                      value={(Array.isArray(draft.additional_images) ? draft.additional_images : []).join(
                        "\n"
                      )}
                      onChange={(e) => {
                        const urls = e.target.value
                          .split("\n")
                          .map((line) => line.trim())
                          .filter(Boolean);
                        setDraft((d) => ({ ...d, additional_images: urls }));
                      }}
                    />
                    {draftMediaUrls.length > 0 ? (
                      <div className="mt-3 max-w-xs">
                        <div className="aspect-[4/5] rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                          <MediaCarousel urls={draftMediaUrls} className="h-full" />
                        </div>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {draftMediaUrls.length} image{draftMediaUrls.length === 1 ? "" : "s"} in gallery
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      <ModalBasic
        id="master-menu-lookup-preview"
        title="Lookup result"
        modalOpen={previewOpen}
        setModalOpen={(open) => {
          if (!open) closePreview();
        }}
        width="4xl"
      >
        {pendingPreview ? (
          <div className="space-y-6">
            <MockListingPreview preview={pendingPreview} />
            {lookupCacheMeta.cached ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Cached from previous search{cachedLookupDateLabel ? ` on ${cachedLookupDateLabel}` : ""}.
                </p>
                <button
                  type="button"
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                  disabled={lookupLoading}
                  onClick={() =>
                    runUpcLookup({
                      upcInput: draft.upc_code,
                      forceRefetch: true,
                    })
                  }
                >
                  Refetch
                </button>
              </div>
            ) : null}
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Apply this data and unlock the full form?
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              {!pendingPreviewHasImage ? (
                <SecondaryButton
                  type="button"
                  onClick={generateLookupPreviewImage}
                  disabled={lookupApplyLoading || lookupImageGenerating}
                >
                  {lookupImageGenerating ? "Generating…" : "AI Generate"}
                </SecondaryButton>
              ) : null}
              <SecondaryButton
                type="button"
                onClick={closePreview}
                disabled={lookupApplyLoading || lookupImageGenerating}
              >
                No
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={applyPendingLookup}
                disabled={lookupApplyLoading || lookupImageGenerating}
              >
                {lookupApplyLoading ? "Applying…" : "Yes, apply"}
              </PrimaryButton>
            </div>
          </div>
        ) : null}
      </ModalBasic>

      <ModalBasic
        id="master-menu-productshot-preview"
        title="Review photo import"
        modalOpen={productshotModalOpen}
        setModalOpen={(open) => {
          if (!open) closeProductshotModal();
        }}
        width="4xl"
      >
        {pendingProductshotPreview ? (
          <div className="space-y-6">
            <MockListingPreview
              preview={pendingProductshotPreview}
              imageLoading={productshotImageGenerating}
            />
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {productshotImageGenerating
                ? "Wait until the picture finishes, then you can apply and open the form."
                : "Continue with these information?"}
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <SecondaryButton
                type="button"
                onClick={closeProductshotModal}
                disabled={productshotApplyLoading}
              >
                No
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={applyProductshot}
                disabled={productshotApplyLoading || productshotImageGenerating}
              >
                {productshotApplyLoading ? "Working…" : "Yes, apply"}
              </PrimaryButton>
            </div>
          </div>
        ) : null}
      </ModalBasic>

      <ModalBasic
        id="master-menu-draft-listing-preview"
        title="Listing preview"
        modalOpen={draftPreviewModalOpen}
        setModalOpen={setDraftPreviewModalOpen}
        width="4xl"
      >
        <div className="space-y-4">
          <MockListingPreview preview={draftListingPreview} />
          <div className="flex justify-end">
            <SecondaryButton type="button" onClick={() => setDraftPreviewModalOpen(false)}>
              Close
            </SecondaryButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default MasterMenuCreateProduct;
